// Contract shared with Signer (sign response) and the public doc-info Lambda
// (GET /d/{ulid}?k=...). Kept in one place so both consumers stay in step.

export const LINEAGE_REASONS = [
	'ALREADY_SIGNED',
	'ENCRYPTED',
	'CERTIFIED',
	'XFA',
	'UNREADABLE',
	'MULTI_SIGNER',
] as const;

export type LineageReason = (typeof LINEAGE_REASONS)[number];

// Plain Spanish for the signer: why the traceability page was not added.
const LINEAGE_REASON_MESSAGES: Record<LineageReason, string> = {
	ALREADY_SIGNED:
		'El documento ya tenía una firma, así que no se pudo agregar la página de trazabilidad sin invalidarla.',
	ENCRYPTED:
		'El documento está protegido con contraseña, así que no se pudo agregar la página de trazabilidad.',
	CERTIFIED:
		'El documento está certificado y no permite modificaciones, así que no se pudo agregar la página de trazabilidad.',
	XFA: 'El documento usa formularios XFA, que no admiten la página de trazabilidad.',
	UNREADABLE:
		'No fue posible leer el documento para agregar la página de trazabilidad.',
	MULTI_SIGNER:
		'La página de trazabilidad solo está disponible cuando firmas tú solo, sin otros firmantes.',
};

export function isLineageReason(value: unknown): value is LineageReason {
	return (
		typeof value === 'string' &&
		(LINEAGE_REASONS as readonly string[]).includes(value)
	);
}

// Returns null when there is nothing to tell the user: no reason, or a reason
// this build does not know about yet (the backend owns the enum).
export function describeLineageReason(
	reason: string | null | undefined,
): string | null {
	if (!isLineageReason(reason)) {
		return null;
	}
	return LINEAGE_REASON_MESSAGES[reason];
}

// Path 2 (public retrieval): why anonymous download was not enabled for a
// document whose signer asked for it.
export const PUBLIC_RETRIEVAL_REASONS = [
	'LINEAGE_NOT_ADDED',
	'CUSTOM_BUCKET_EXCLUDED',
	'COPY_FAILED',
] as const;

export type PublicRetrievalReason = (typeof PUBLIC_RETRIEVAL_REASONS)[number];

const PUBLIC_RETRIEVAL_REASON_MESSAGES: Record<PublicRetrievalReason, string> =
	{
		LINEAGE_NOT_ADDED:
			'La descarga pública no se habilitó porque no se agregó la página de trazabilidad.',
		CUSTOM_BUCKET_EXCLUDED:
			'La descarga pública no está disponible para los documentos que se guardan en el almacenamiento propio de tu organización.',
		COPY_FAILED:
			'No fue posible preparar la copia pública del documento firmado, así que la descarga pública quedó deshabilitada.',
	};

export function isPublicRetrievalReason(
	value: unknown,
): value is PublicRetrievalReason {
	return (
		typeof value === 'string' &&
		(PUBLIC_RETRIEVAL_REASONS as readonly string[]).includes(value)
	);
}

// Same contract as describeLineageReason: null when there is nothing to say, or
// when the backend sent a reason this build does not know about yet.
export function describePublicRetrievalReason(
	reason: string | null | undefined,
): string | null {
	if (!isPublicRetrievalReason(reason)) {
		return null;
	}
	return PUBLIC_RETRIEVAL_REASON_MESSAGES[reason];
}

export type DocumentLineageSignature = {
	name: string;
	cert_serial: string;
	signed_at: string;
};

// 'withdrawn' is an HTTP 200 with every metadata field populated: the owner
// revoked public retrieval, so the record still renders but there is nothing to
// download. 'not_found' never reaches this type (it is a 404).
export type DocumentLineageState = 'ok' | 'withdrawn';

export type DocumentLineageInfo = {
	state: DocumentLineageState;
	document_name: string;
	page_count: number;
	signatures: DocumentLineageSignature[];
	sha256_original: string;
	sha256_final: string;
	lineage_added_at: string;
	public_retrieval: boolean;
	// Presigned, 5-minute TTL, Content-Disposition: attachment. Null whenever
	// public retrieval is off, withdrawn or was never requested.
	download_url: string | null;
};

function isDocumentLineageSignature(
	value: unknown,
): value is DocumentLineageSignature {
	return (
		typeof value === 'object' &&
		value !== null &&
		'name' in value &&
		typeof value.name === 'string' &&
		'cert_serial' in value &&
		typeof value.cert_serial === 'string' &&
		'signed_at' in value &&
		typeof value.signed_at === 'string'
	);
}

// Validates the metadata every backend sends. The path-2 fields are checked
// separately by parseDocumentLineageInfo, which defaults them: a backend that
// predates public retrieval must keep rendering the card, not fail the guard.
function hasLineageMetadata(value: unknown): value is Omit<
	DocumentLineageInfo,
	'state' | 'public_retrieval' | 'download_url'
> & {
	state: DocumentLineageState;
} {
	return (
		typeof value === 'object' &&
		value !== null &&
		'state' in value &&
		(value.state === 'ok' || value.state === 'withdrawn') &&
		'document_name' in value &&
		typeof value.document_name === 'string' &&
		'page_count' in value &&
		typeof value.page_count === 'number' &&
		'signatures' in value &&
		Array.isArray(value.signatures) &&
		value.signatures.every(isDocumentLineageSignature) &&
		'sha256_original' in value &&
		typeof value.sha256_original === 'string' &&
		'sha256_final' in value &&
		typeof value.sha256_final === 'string' &&
		'lineage_added_at' in value &&
		typeof value.lineage_added_at === 'string'
	);
}

function readOptionalBoolean(
	value: Record<string, unknown>,
	key: string,
): boolean | 'invalid' {
	const raw = value[key];
	if (raw === undefined || raw === null) return false;
	return typeof raw === 'boolean' ? raw : 'invalid';
}

function readOptionalString(
	value: Record<string, unknown>,
	key: string,
): string | null | 'invalid' {
	const raw = value[key];
	if (raw === undefined || raw === null) return null;
	return typeof raw === 'string' ? raw : 'invalid';
}

// Returns null when the body does not match the contract. A card rendered half
// empty would be worse than an error: this is a verification surface.
export function parseDocumentLineageInfo(
	value: unknown,
): DocumentLineageInfo | null {
	if (!hasLineageMetadata(value)) {
		return null;
	}

	const raw = value as unknown as Record<string, unknown>;
	const publicRetrieval = readOptionalBoolean(raw, 'public_retrieval');
	const downloadUrl = readOptionalString(raw, 'download_url');

	if (publicRetrieval === 'invalid' || downloadUrl === 'invalid') {
		return null;
	}

	// The server owns the decision; a download URL without the flag, or a
	// 'withdrawn' record carrying one, is treated as "no download".
	const isDownloadable = publicRetrieval && value.state === 'ok';

	return {
		state: value.state,
		document_name: value.document_name,
		page_count: value.page_count,
		signatures: value.signatures,
		sha256_original: value.sha256_original,
		sha256_final: value.sha256_final,
		lineage_added_at: value.lineage_added_at,
		public_retrieval: isDownloadable,
		download_url: isDownloadable ? downloadUrl : null,
	};
}
